import { useTranslation } from "react-i18next";
import { QrCode } from "lucide-react";
import { FuellyLogo } from "@/components/FuellyBrand";
import { PLATFORM } from "@/data/platform";
import { HOME } from "@/constants/testIds";

/**
 * The root of fuelly.cloud **in development only**.
 *
 * In production nginx answers `/` with `public/home.html`, a standalone static
 * page that says what Fuelly is — this screen never renders there. That split
 * exists because a client-rendered stub is invisible to anyone who does not run
 * JavaScript, which is how Google's brand review came to report that our home
 * page was behind a login and explained nothing. See `frontend/nginx.conf`.
 *
 * Deliberately a stub. `/` used to redirect to the first tenant, which made the
 * platform's own address look like one restaurant's storefront — and gave anyone
 * who guessed the domain a menu they were never meant to browse. Guests arrive
 * through the QR code on their table, at /r/:slug; nobody is supposed to land here.
 *
 * Kept free of anything a search engine would want: no menu, no prices, no tenant
 * names. It is also noindex'd (public/robots.txt and the meta tag in index.html).
 */
const Landing = () => {
  const { t } = useTranslation();
  return (
    <div className="App">
      <div className="app-frame">
        <div
          data-testid={HOME.landing}
          className="min-h-screen px-8 flex flex-col items-center justify-center text-center"
        >
          <FuellyLogo size={56} className="mb-5" />

          <h1 className="text-2xl font-black tracking-tight text-neutral-900">{PLATFORM.name}</h1>
          <p className="text-sm text-neutral-500 mt-1.5">{t("landing.tagline")}</p>

          <div className="mt-9 flex items-center gap-2.5 text-neutral-400">
            <QrCode size={16} />
            <span
              data-testid={HOME.landingHint}
              className="text-[11px] font-mono uppercase tracking-[0.2em]"
            >
              {t("landing.hint")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Landing;
